import KaTeX from 'katex'
import { setupEngine, engineReady, toSpeech } from 'speech-rule-engine'

type RenderOptions = {
  displayMode?: boolean
}

type RenderResult = {
  html: string
  speakable: string
  isPlainNumber: boolean
}

/**
 * Initialise SRE once at module load. The engine loads its rule data
 * asynchronously; by the time a student reaches the first question it will be
 * ready. Before ready, toSpeech returns '' — MathMLComponent falls back to
 * exposing KaTeX's own MathML so screen readers can still traverse it.
 */
setupEngine({ locale: 'en', domain: 'mathspeak', style: 'default', modality: 'speech' })
engineReady().catch(() => {})

/**
 * Strip leading/trailing LaTeX whitespace commands (e.g. "\ ", "\,", "\;", "\:",
 * "\!", "\quad", "\qquad") along with regular whitespace. Authored answers
 * sometimes include trailing spacing like "1,055\ " which otherwise prevents
 * the plain-number detection from matching and leaks into the aria-label.
 */
function stripLatexWhitespace(tex: string): string {
  const pattern = /^(?:\s|\\[ ,;:!]|\\q?quad)+|(?:\s|\\[ ,;:!]|\\q?quad)+$/g
  return tex.replace(pattern, '')
}

/**
 * Checks if LaTeX is just a formatted number (not a mathematical expression).
 * Matches integers, thousands-separated values (commas or {,}), and decimals —
 * with optional LaTeX math-mode delimiters ($...$) or escaped currency (\$...).
 * e.g. "29,889", "1,097.6", "5{,}625", "40", "0.75", "\$183.00", "\$66,795.00"
 */
function isPlainFormattedNumber(tex: string): boolean {
  // Allow an optional leading \$ or $ and optional trailing $, with digits,
  // commas, {,} escapes, spaces, and an optional decimal part.
  return /^(?:\\?\$)?[\d,{}\s]+(\.\d+)?(?:\$)?$/.test(stripLatexWhitespace(tex))
}

/**
 * Checks if LaTeX is plain letters only (e.g. h, x, ABC, DEF) —
 * single-variable names and geometry labels that read better as plain text.
 */
function isPlainLetters(tex: string): boolean {
  return /^[a-zA-Z]+$/.test(stripLatexWhitespace(tex))
}

/**
 * Extract the MathML block from KaTeX's rendered HTML output.
 * KaTeX always emits a <math> element inside the .katex-mathml span.
 */
function extractMathML(html: string): string | null {
  return html.match(/<math[\s\S]*?<\/math>/)?.[0] ?? null
}

/**
 * Render TeX into KaTeX HTML and a human-readable speakable string for ARIA
 * labels, powered by Speech Rule Engine (SRE).
 *
 * SRE operates on the MathML that KaTeX already generates — meaning it
 * handles everything KaTeX can render: fractions, matrices, piecewise
 * functions, inequalities, and all standard STAAR math.
 *
 * For plain formatted numbers (e.g. "29,889", "1,097.6") and single-letter
 * variables, returns isPlainNumber: true so consumers render them as plain
 * text rather than math markup.
 *
 * @param tex     - The TeX/LaTeX string to render
 * @param options - KaTeX rendering options
 * @returns { html, speakable, isPlainNumber }
 */
export const renderKatexAccessible = (tex: string, options: RenderOptions = {}): RenderResult => {
  // Plain numbers (including decimals like 1,097.6) render as text — no math markup needed
  if (isPlainFormattedNumber(tex)) {
    const trimmed = stripLatexWhitespace(tex)
    let speakable: string
    if (trimmed.startsWith('\\$')) {
      // Escaped dollar = LaTeX currency amount (\$183.00 → $183.00).
      // Keep the $ so screen readers announce it as a dollar amount.
      speakable = trimmed.replace(/\\\$/g, '$').replace(/\{,\}/g, ',')
    } else {
      // Bare $ signs are LaTeX math-mode delimiters — strip them.
      speakable = trimmed.replace(/\{,\}/g, ',').replace(/^\$+|\$+$/g, '')
    }
    return { html: '', speakable: speakable.trim(), isPlainNumber: true }
  }

  // Single-letter variables (h, x, DEF) read more naturally as plain text
  if (isPlainLetters(tex)) {
    return {
      html: '',
      speakable: stripLatexWhitespace(tex),
      isPlainNumber: true,
    }
  }

  // Render KaTeX HTML (used for visual display and as the source of MathML)
  const html = KaTeX.renderToString(tex, {
    displayMode: Boolean(options.displayMode),
    throwOnError: false,
  })

  // SRE reads directly from KaTeX's MathML output — no custom LaTeX parsing.
  // toSpeech returns '' if the engine is not yet ready or the expression is
  // unsupported; MathMLComponent exposes the raw MathML as a fallback in that case.
  let speakable = ''
  const mathml = extractMathML(html)
  if (mathml) {
    speakable = toSpeech(mathml) || ''
  }

  return { html, speakable, isPlainNumber: false }
}
